import type { ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { usePosterOverview } from './use-poster-overview';
import { PageContainer } from '@/components/PageContainer';
import { ArrowRightIcon, BadgeCheckIcon } from '@/components/ui/Icons';

/**
 * The frame every job poster page sits in.
 *
 * The third console, built like the other two: one rail carrying live counts,
 * so the queue of people waiting on a decision is never out of sight.
 */

interface Tab {
  label: string;
  to: string;
  badge?: number;
}

export function PosterShell({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  const overview = usePosterOverview();
  const data = overview.data;

  const tabs: Tab[] = [
    { label: 'Overview', to: '/poster' },
    { label: 'Live', to: '/tracking' },
    { label: 'Money', to: '/money' },
    { label: 'My jobs', to: '/my-jobs', badge: data?.draftJobs ?? 0 },
    {
      label: 'Applicants',
      to: '/applicants',
      badge: (data?.newApplications ?? 0) + (data?.openBids ?? 0),
    },
  ];

  const unverified = data ? data.verificationStatus !== 'APPROVED' : false;

  return (
    <PageContainer>
      {unverified && (
        <div className="mb-6 flex flex-wrap items-center gap-3 rounded-2xl border border-sun-200 bg-sun-50 px-5 py-4">
          <BadgeCheckIcon className="size-5 shrink-0 text-sun-700" />
          <p className="flex-1 text-sm font-medium text-sun-700">
            Your business is not verified yet. You can post and hire, but guardians are far more
            likely to approve their teen for a verified employer.
          </p>
          <Link
            to="/profile"
            className="inline-flex items-center gap-1.5 text-sm font-bold text-sun-700 hover:underline"
          >
            Complete profile
            <ArrowRightIcon className="size-4" />
          </Link>
        </div>
      )}

      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold tracking-[0.14em] text-brand-600 uppercase">
            Employer console
          </p>
          <h1 className="mt-2 text-2xl font-extrabold text-ink sm:text-3xl">{title}</h1>
          {description && (
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-500">{description}</p>
          )}
        </div>
        {action ?? (
          <Link
            to="/jobs/new"
            className="inline-flex items-center gap-2 rounded-xl bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white shadow-soft transition hover:bg-brand-700"
          >
            Post a job
            <ArrowRightIcon className="size-4" />
          </Link>
        )}
      </header>

      <nav
        aria-label="Employer console"
        className="mt-6 -mx-1 flex gap-1 overflow-x-auto border-b border-line pb-px"
      >
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end
            className={({ isActive }) =>
              `relative flex shrink-0 items-center gap-2 rounded-t-lg px-4 py-3 text-sm font-semibold whitespace-nowrap transition ${
                isActive
                  ? 'text-brand-700 after:absolute after:inset-x-2 after:-bottom-px after:h-0.5 after:rounded-full after:bg-brand-600'
                  : 'text-slate-500 hover:text-ink'
              }`
            }
          >
            {tab.label}
            {tab.badge !== undefined && tab.badge > 0 && (
              <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[0.65rem] font-bold text-white">
                {tab.badge}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="mt-8">{children}</div>
    </PageContainer>
  );
}
